import { createFileRoute } from "@tanstack/react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
	getAllCoursesOptions,
	createCourseMutation,
	deleteCourseMutation,
	archiveCourseMutation,
	unarchiveCourseMutation,
	getAllCoursesQueryKey,
	getSubjectsOptions,
	getLevelsOptions,
	getSchoolsOptions,
	getTimeSlotsOptions,
	getAllTeachersOptions,
} from "@/lib/client/@tanstack/react-query.gen";
import {
	Card,
	CardContent,
	CardHeader,
	CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
	DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import {
	Plus,
	Trash2,
	Clock,
	Euro,
	Users,
	BookOpen,
	Calendar,
	Eye,
	AlertTriangle,
	Archive,
	ArchiveRestore,
} from "lucide-react";
import { useNavigate } from "@tanstack/react-router";
import { useMemo, useState, useCallback } from "react";
import { useForm } from "@tanstack/react-form";
import {
	getSubjectLabel,
	getLevelLabel,
	getSchoolLabel,
	getTimeSlot,
} from "@/stores/enum-store";
import type { ColumnDef } from "@tanstack/react-table";
import {
	DataTable,
	DataTableColumnHeader,
	DataTableToolbar,
	DataTableFilterFaceted,
	DataTableFilterText,
} from "@/components/data-table";
import { ArchiveCourseDialog } from "@/components/ArchiveCourseDialog";

export const Route = createFileRoute("/admin/courses/")({
	component: CoursesManagementPage,
});

type CourseStatus = "ACTIVE" | "PAUSED" | "ARCHIVED";

const getCourseStatus = (course: {
	archivedAt?: string;
	startDate?: string;
}): CourseStatus =>
	course.archivedAt ? "ARCHIVED" : course.startDate ? "ACTIVE" : "PAUSED";

const courseStatusLabels: Record<CourseStatus, string> = {
	ACTIVE: "Actif",
	PAUSED: "En pause",
	ARCHIVED: "Archivé",
};

function CoursesManagementPage() {
	const queryClient = useQueryClient();
	const navigate = useNavigate();
	const [createDialogOpen, setCreateDialogOpen] = useState(false);
	const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
	const [courseToDelete, setCourseToDelete] = useState<NonNullable<typeof coursesData>[number] | null>(null);
	const [archiveDialogOpen, setArchiveDialogOpen] = useState(false);
	const [courseToArchive, setCourseToArchive] = useState<NonNullable<typeof coursesData>[number] | null>(null);

	const { data: coursesData, isLoading } = useQuery(
		getAllCoursesOptions({ query: { includeArchived: true } }),
	);

	const { data: subjectsData } = useQuery(getSubjectsOptions());
	const subjects =
		(subjectsData as any as Array<{ value: string; label: string }>) || [];

	const { data: levelsData } = useQuery(getLevelsOptions());
	const levels =
		(levelsData as any as Array<{ value: string; label: string }>) || [];

	const { data: schoolsData } = useQuery(getSchoolsOptions());
	const schools =
		(schoolsData as any as Array<{ value: string; label: string }>) || [];

	const { data: timeSlotsData } = useQuery(getTimeSlotsOptions());
	const timeSlots =
		(timeSlotsData as any as Array<{
			value: string;
			label: string;
			dayOfWeek: string;
			startTime: { hour: number; minute: number; second: number; nano: number };
			endTime: { hour: number; minute: number; second: number; nano: number };
		}>) || [];

	const { data: teachersData } = useQuery(getAllTeachersOptions());
	const teachers = teachersData || [];

	const createCourseMut = useMutation({
		...createCourseMutation(),
		onSuccess: () => {
			toast.success("Cours créé avec succès");
			queryClient.invalidateQueries({ queryKey: getAllCoursesQueryKey() });
			setCreateDialogOpen(false);
		},
		onError: () => {
			toast.error("Erreur lors de la création du cours");
		},
	});

	const deleteCourseMut = useMutation({
		...deleteCourseMutation(),
		onSuccess: () => {
			toast.success("Cours supprimé avec succès");
			queryClient.invalidateQueries({ queryKey: getAllCoursesQueryKey() });
			setDeleteDialogOpen(false);
			setCourseToDelete(null);
		},
		onError: () => {
			toast.error("Erreur lors de la suppression");
		},
	});

	const archiveCourseMut = useMutation({
		...archiveCourseMutation(),
		onSuccess: () => {
			toast.success("Cours archivé : inscriptions clôturées et présences conservées");
			queryClient.invalidateQueries({ queryKey: getAllCoursesQueryKey() });
			setArchiveDialogOpen(false);
			setCourseToArchive(null);
		},
		onError: () => {
			toast.error("Erreur lors de l'archivage du cours");
		},
	});

	const unarchiveCourseMut = useMutation({
		...unarchiveCourseMutation(),
		onSuccess: () => {
			toast.success("Cours désarchivé");
			queryClient.invalidateQueries({ queryKey: getAllCoursesQueryKey() });
		},
		onError: () => {
			toast.error("Erreur lors du désarchivage du cours");
		},
	});

	const form = useForm({
		defaultValues: {
			subject: "",
			level: "",
			school: "",
			timeSlot: "",
			pricePerSession: 25,
			startDate: "",
			teacherId: "",
		},
		onSubmit: async ({ value }) => {
			const selectedSlot = timeSlots.find(
				(slot) => slot.value === value.timeSlot,
			);

			if (!selectedSlot) {
				toast.error("Créneau invalide");
				return;
			}

			createCourseMut.mutate({
				body: {
					subject: value.subject,
					level: value.level,
					school: value.school,
					pricePerSession: value.pricePerSession,
					startDate: value.startDate || undefined,
					timeSlot: selectedSlot.value as any,
					teacherId: value.teacherId || undefined,
				},
			});
		},
	});

	const openDeleteDialog = useCallback((course: NonNullable<typeof coursesData>[number]) => {
		setCourseToDelete(course);
		setDeleteDialogOpen(true);
	}, []);

	const handleDeleteCourse = () => {
		if (!courseToDelete?.id) return;
		deleteCourseMut.mutate({
			path: { id: courseToDelete.id },
		});
	};

	const openArchiveDialog = useCallback((course: NonNullable<typeof coursesData>[number]) => {
		setCourseToArchive(course);
		setArchiveDialogOpen(true);
	}, []);

	const handleArchiveCourse = (archiveDate: string) => {
		if (!courseToArchive?.id) return;
		archiveCourseMut.mutate({
			path: { id: courseToArchive.id },
			body: { archiveDate: archiveDate || undefined },
		});
	};

	const handleUnarchiveCourse = useCallback(
		(course: NonNullable<typeof coursesData>[number]) => {
			unarchiveCourseMut.mutate({ path: { id: course.id } });
		},
		[unarchiveCourseMut],
	);


	const dayOfWeekLabels: Record<string, string> = {
		MONDAY: "Lundi",
		TUESDAY: "Mardi",
		WEDNESDAY: "Mercredi",
		THURSDAY: "Jeudi",
		FRIDAY: "Vendredi",
		SATURDAY: "Samedi",
		SUNDAY: "Dimanche",
	};

	type CourseRow = NonNullable<typeof coursesData>[number];

	const columns = useMemo<ColumnDef<CourseRow, unknown>[]>(
		() => [
			{
				id: "subject",
				accessorFn: (row) => getSubjectLabel(row.subject),
				header: ({ column }) => (
					<DataTableColumnHeader column={column} title="Matière" />
				),
				cell: ({ row }) => (
					<div>
						{row.original.archivedAt ? (
							<Badge variant="outline" className="text-xs mb-1 text-gray-600 border-gray-300 bg-gray-100">
								<Archive className="h-3 w-3 mr-1" />
								Archivé
							</Badge>
						) : (
							!row.original.startDate && (
								<Badge variant="destructive" className="text-xs mb-1">
									En pause
								</Badge>
							)
						)}
						<div className={`flex items-center gap-2 ${row.original.archivedAt ? "text-gray-500" : ""}`}>
							<BookOpen className={`h-4 w-4 ${row.original.archivedAt ? "text-gray-400" : "text-blue-600"}`} />
							<span className="font-medium">
								{getSubjectLabel(row.original.subject)}
							</span>
						</div>
					</div>
				),
				meta: { label: "Matière" },
			},
			{
				id: "level",
				accessorFn: (row) => getLevelLabel(row.level),
				header: ({ column }) => (
					<DataTableColumnHeader column={column} title="Niveau" />
				),
				cell: ({ row }) => (
					<Badge variant="secondary">
						{getLevelLabel(row.original.level)}
					</Badge>
				),
				meta: { label: "Niveau" },
			},
			{
				id: "school",
				accessorFn: (row) => getSchoolLabel(row.school),
				header: ({ column }) => (
					<DataTableColumnHeader column={column} title="Établissement" />
				),
				cell: ({ row }) => (
					<span className="text-sm text-gray-600">
						{getSchoolLabel(row.original.school)}
					</span>
				),
				filterFn: (row, id, value: string[]) =>
					value.includes(String(row.getValue(id))),
				meta: { label: "Établissement" },
			},
			{
				id: "teacherName",
				accessorKey: "teacherName",
				header: ({ column }) => (
					<DataTableColumnHeader column={column} title="Professeur" />
				),
				cell: ({ row }) => (
					<span className="text-sm text-gray-600">
						{row.original.teacherName || "-"}
					</span>
				),
				meta: { label: "Professeur" },
			},
			{
				id: "timeSlot",
				accessorKey: "timeSlot",
				header: ({ column }) => (
					<DataTableColumnHeader column={column} title="Horaires" />
				),
				cell: ({ row }) => {
					const course = row.original;
					return (
						<div className="space-y-1">
							{getTimeSlot(course.timeSlot) != null && (
								<>
									<div className="flex items-center gap-1 text-sm">
										<Calendar className="h-3 w-3" />
										{
											dayOfWeekLabels[
												getTimeSlot(course.timeSlot)!.dayOfWeek!
											]
										}
									</div>

									<div className="flex items-center gap-1 text-sm text-gray-600">
										<Clock className="h-3 w-3" />
										{getTimeSlot(course.timeSlot)!.startTime}-
										{getTimeSlot(course.timeSlot)!.endTime}
									</div>
								</>
							)}

							{getTimeSlot(course.timeSlot) == null && (
								<div className="flex items-center gap-1 text-sm text-gray-400">
									<Clock className="h-3 w-3" />
									Horaire non défini
								</div>
							)}
						</div>
					);
				},
				enableSorting: false,
				meta: {
					label: "Horaires",
					textValue: (row: CourseRow) => {
						const ts = getTimeSlot(row.timeSlot);
						if (!ts) return "Horaire non défini";
						return `${dayOfWeekLabels[ts.dayOfWeek!] || ts.dayOfWeek} ${ts.startTime}-${ts.endTime}`;
					},
				},
			},
			{
				id: "pricePerSession",
				accessorKey: "pricePerSession",
				header: ({ column }) => (
					<DataTableColumnHeader column={column} title="Prix" />
				),
				cell: ({ row }) => (
					<div className="flex items-center gap-1">
						<Euro className="h-3 w-3 text-green-600" />
						<span className="font-medium">
							{row.original.pricePerSession?.toFixed(2)} €
						</span>
					</div>
				),
				meta: {
					label: "Prix",
					textValue: (row: CourseRow) => `${row.pricePerSession?.toFixed(2)} €`,
				},
			},
			{
				id: "enrollments",
				accessorFn: (row) => row.enrollments?.length || 0,
				header: ({ column }) => (
					<DataTableColumnHeader column={column} title="Élèves" />
				),
				cell: ({ row }) => (
					<div className="flex items-center gap-1">
						<Users className="h-3 w-3 text-gray-400" />
						<Badge variant="outline">
							{row.original.enrollments?.length || 0} inscrit(s)
						</Badge>
					</div>
				),
				meta: { label: "Élèves" },
			},
			{
				id: "status",
				accessorFn: (row) => getCourseStatus(row),
				header: () => null,
				cell: () => null,
				enableSorting: false,
				enableHiding: true,
				filterFn: (row, id, value: string[]) =>
					value.includes(String(row.getValue(id))),
				meta: {
					label: "Statut",
					textValue: (row: CourseRow) => courseStatusLabels[getCourseStatus(row)],
				},
			},
			{
				id: "actions",
				header: () => <span>Actions</span>,
				cell: ({ row }) => {
					const course = row.original;
					return (
						<div className="flex items-center gap-2">
							<Button
								size="sm"
								variant="outline"
								onClick={() =>
									navigate({ to: `/admin/courses/${course.id}` })
								}
								title="Voir le cours"
							>
								<Eye className="h-3 w-3" />
							</Button>
							{course.archivedAt ? (
								<Button
									size="sm"
									variant="outline"
									onClick={() => handleUnarchiveCourse(course)}
									disabled={unarchiveCourseMut.isPending}
									title="Désarchiver le cours"
								>
									<ArchiveRestore className="h-3 w-3" />
								</Button>
							) : (
								<Button
									size="sm"
									variant="outline"
									onClick={() => openArchiveDialog(course)}
									disabled={archiveCourseMut.isPending}
									title="Archiver le cours (fin d'année)"
								>
									<Archive className="h-3 w-3" />
								</Button>
							)}
							<Button
								size="sm"
								variant="destructive"
								onClick={() => openDeleteDialog(course)}
								disabled={deleteCourseMut.isPending}
								title="Supprimer le cours"
							>
								<Trash2 className="h-3 w-3" />
							</Button>
						</div>
					);
				},
				enableSorting: false,
				meta: { label: "Actions" },
			},
		],
		[
			dayOfWeekLabels,
			navigate,
			deleteCourseMut.isPending,
			archiveCourseMut.isPending,
			unarchiveCourseMut.isPending,
			openDeleteDialog,
			openArchiveDialog,
			handleUnarchiveCourse,
		],
	);

	const schoolFilterOptions = useMemo(
		() =>
			schools.map((school) => ({
				label: school.label,
				value: school.label,
			})),
		[schools],
	);

	const statusFilterOptions = useMemo(
		() => [
			{ label: "Actif", value: "ACTIVE" },
			{ label: "En pause", value: "PAUSED" },
			{ label: "Archivé", value: "ARCHIVED" },
		],
		[],
	);

	// Archived courses are hidden by default; they can be shown via the status filter
	const initialColumnFilters = useMemo(
		() => [{ id: "status", value: ["ACTIVE", "PAUSED"] }],
		[],
	);

	if (isLoading) {
		return (
			<div className="flex items-center justify-center h-64">
				<div className="text-center">
					<div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900 mx-auto"></div>
					<p className="mt-4 text-gray-600">Chargement des cours...</p>
				</div>
			</div>
		);
	}

	return (
		<div className="space-y-6">
			<div className="flex justify-between items-center">
				<div>
					<h1 className="text-3xl font-bold">Gestion des cours</h1>
					<p className="text-gray-600 mt-2">
						Créez et gérez les cours de soutien scolaire
					</p>
				</div>
				<Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
					<DialogTrigger asChild>
						<Button>
							<Plus className="mr-2 h-4 w-4" />
							Nouveau cours
						</Button>
					</DialogTrigger>
					<DialogContent className="max-w-2xl">
						<DialogHeader>
							<DialogTitle>Créer un nouveau cours</DialogTitle>
							<DialogDescription>
								Définissez les informations du nouveau cours
							</DialogDescription>
						</DialogHeader>
						<form
							onSubmit={(e) => {
								e.preventDefault();
								form.handleSubmit();
							}}
							className="space-y-4"
						>
							<div className="grid grid-cols-2 gap-4">
								<form.Field
									name="subject"
									children={(field) => (
										<div>
											<Label htmlFor={field.name}>Matière</Label>
											<Select
												value={field.state.value}
												onValueChange={(value) => field.handleChange(value)}
											>
												<SelectTrigger>
													<SelectValue placeholder="Sélectionner une matière" />
												</SelectTrigger>
												<SelectContent>
													{subjects.map((subject) => (
														<SelectItem
															key={subject.value}
															value={subject.value}
														>
															{subject.label}
														</SelectItem>
													))}
												</SelectContent>
											</Select>
										</div>
									)}
								/>
								<form.Field
									name="level"
									children={(field) => (
										<div>
											<Label htmlFor={field.name}>Niveau</Label>
											<Select
												value={field.state.value}
												onValueChange={(value) => field.handleChange(value)}
											>
												<SelectTrigger>
													<SelectValue placeholder="Sélectionner un niveau" />
												</SelectTrigger>
												<SelectContent>
													{levels.map((level) => (
														<SelectItem key={level.value} value={level.value}>
															{level.label}
														</SelectItem>
													))}
												</SelectContent>
											</Select>
										</div>
									)}
								/>
							</div>

							<form.Field
								name="school"
								children={(field) => (
									<div>
										<Label htmlFor={field.name}>Établissement</Label>
										<Select
											value={field.state.value}
											onValueChange={(value) => field.handleChange(value)}
										>
											<SelectTrigger>
												<SelectValue placeholder="Sélectionner un établissement" />
											</SelectTrigger>
											<SelectContent>
												{schools.map((school) => (
													<SelectItem key={school.value} value={school.value}>
														{school.label}
													</SelectItem>
												))}
											</SelectContent>
										</Select>
									</div>
								)}
							/>

							<form.Field
								name="timeSlot"
								children={(field) => (
									<div>
										<Label htmlFor={field.name}>Créneau</Label>
										<Select
											value={field.state.value}
											onValueChange={(value) => field.handleChange(value)}
										>
											<SelectTrigger>
												<SelectValue placeholder="Sélectionner un créneau" />
											</SelectTrigger>
											<SelectContent>
												{timeSlots.map((slot) => (
													<SelectItem key={slot.value} value={slot.value}>
														{slot.label}
													</SelectItem>
												))}
											</SelectContent>
										</Select>
									</div>
								)}
							/>

							<form.Field
								name="teacherId"
								children={(field) => (
									<div>
										<Label htmlFor={field.name}>Professeur (optionnel)</Label>
										<Select
											value={field.state.value}
											onValueChange={(value) => field.handleChange(value)}
										>
											<SelectTrigger>
												<SelectValue placeholder="Sélectionner un professeur" />
											</SelectTrigger>
											<SelectContent>
												{teachers.map((teacher) => (
													<SelectItem key={teacher.id} value={teacher.id}>
														{teacher.fullName}
													</SelectItem>
												))}
											</SelectContent>
										</Select>
									</div>
								)}
							/>

							<form.Field
								name="pricePerSession"
								children={(field) => (
									<div>
										<Label htmlFor={field.name}>Prix par séance (€)</Label>
										<Input
											id={field.name}
											type="number"
											min={0}
											step="0.01"
											value={field.state.value}
											onChange={(e) =>
												field.handleChange(parseFloat(e.target.value) || 0)
											}
										/>
									</div>
								)}
							/>

							<form.Field
								name="startDate"
								children={(field) => (
									<div>
										<Label htmlFor={field.name}>
											Date de début du cours (optionnel)
										</Label>
										<Input
											id={field.name}
											type="date"
											value={field.state.value}
											onChange={(e) => field.handleChange(e.target.value)}
										/>
										<p className="text-xs text-gray-500 mt-1">
											Laissez vide si le cours est en pause
										</p>
									</div>
								)}
							/>

							<DialogFooter>
								<Button
									variant="outline"
									type="button"
									onClick={() => setCreateDialogOpen(false)}
								>
									Annuler
								</Button>
								<Button type="submit" disabled={createCourseMut.isPending}>
									{createCourseMut.isPending ? "Création..." : "Créer le cours"}
								</Button>
							</DialogFooter>
						</form>
					</DialogContent>
				</Dialog>
			</div>

			<Card>
				<CardHeader>
					<CardTitle>Liste des cours</CardTitle>
				</CardHeader>
				<CardContent>
					<DataTable
						columns={columns}
						data={coursesData || []}
						emptyMessage="Aucun cours créé"
						initialColumnFilters={initialColumnFilters}
						toolbar={(table) => (
							<DataTableToolbar table={table} showExportExcel exportFilename="cours">
								<DataTableFilterText
									column={table.getColumn("subject")!}
									placeholder="Rechercher une matière..."
								/>
								<DataTableFilterFaceted
									column={table.getColumn("school")!}
									title="Établissement"
									options={schoolFilterOptions}
								/>
								<DataTableFilterFaceted
									column={table.getColumn("status")!}
									title="Statut"
									options={statusFilterOptions}
								/>
							</DataTableToolbar>
						)}
					/>
				</CardContent>
			</Card>

			<Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
				<DialogContent>
					<DialogHeader>
						<DialogTitle>Supprimer le cours</DialogTitle>
						<DialogDescription>
							{courseToDelete && (courseToDelete.enrollments?.length ?? 0) > 0 ? (
								<span className="flex items-start gap-2 mt-2">
									<AlertTriangle className="h-5 w-5 text-amber-500 shrink-0 mt-0.5" />
									<span>
										Ce cours a actuellement <strong>{courseToDelete.enrollments?.length} élève(s) inscrit(s)</strong>.
										En supprimant ce cours, tous les élèves seront automatiquement désinscrit.
									</span>
								</span>
							) : (
								"Êtes-vous sûr de vouloir supprimer ce cours ?"
							)}
						</DialogDescription>
					</DialogHeader>
					<DialogFooter>
						<Button
							variant="outline"
							onClick={() => setDeleteDialogOpen(false)}
						>
							Annuler
						</Button>
						<Button variant="destructive" onClick={handleDeleteCourse}>
							Supprimer
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>

			<ArchiveCourseDialog
				open={archiveDialogOpen}
				onOpenChange={setArchiveDialogOpen}
				courseName={
					courseToArchive
						? `${getSubjectLabel(courseToArchive.subject)} - ${getLevelLabel(courseToArchive.level)} (${getSchoolLabel(courseToArchive.school)})`
						: ""
				}
				activeEnrollmentCount={courseToArchive?.enrollments?.length ?? 0}
				isPending={archiveCourseMut.isPending}
				onConfirm={handleArchiveCourse}
			/>
		</div>
	);
}
